import { useEffect, useRef, useState } from 'react';
import { useApolloClient } from '@apollo/client';
import { PageContainerBySlugDocument } from '../../../features/custom-page/WebsiteEditorCustomPage.graphql.generated';
import { AccommodationHotelsDocument } from '../Accommodations.graphql.generated';
import { useAutoPopulation } from '@shared/utils/accommodations';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { addError } from '@shared/utils/logger';
import { useUpdateWebsitePreview } from '@apps/admin/routes/WebsiteEditor/hooks/useUpdateWebsitePreview';
import type { AccommodationsContext, AutoPopulationPollingStatus } from '@shared/utils/accommodations/autoPopulation/types';

type UseAutoPopulationWithDataRefetchOptions = Readonly<{
  /** Mode of operation — 'auto' starts + polls, 'poll-only' just polls existing operations */
  mode: 'auto' | 'poll-only';
  /** Context identifier for error logging */
  context: AccommodationsContext;
  /** Whether to validate if enough time has elapsed since event creation */
  validateIsEnoughTimeElapsed?: boolean;
  /** Whether to skip polling */
  skipPolling?: boolean;
}>;

/**
 * Wrapper around `useAutoPopulation` for use inside the Accommodations WebsiteEditor.
 *
 * Adds two behaviors that only editor consumers need:
 * 1. **Auto-refetch** — refetches `AccommodationHotels` and `PageContainerBySlug` queries
 *    via Apollo client when polling completes, so the cache is fresh before the UI updates.
 * 2. **Flash prevention** — holds `status` at `'PENDING'` until the refetch resolves,
 *    preventing a brief flash of stale UI between polling completion and data arrival.
 */
export const useAutoPopulationWithDataRefetch = (options: UseAutoPopulationWithDataRefetchOptions) => {
  const { mode, context, validateIsEnoughTimeElapsed, skipPolling } = options;
  const client = useApolloClient();
  const { updateWebsitePreview } = useUpdateWebsitePreview();
  const [isRefetching, setIsRefetching] = useState(false);

  const handleOnComplete = useEventCallback(async () => {
    setIsRefetching(true);
    try {
      await client.refetchQueries({
        include: [AccommodationHotelsDocument, PageContainerBySlugDocument],
      });
      updateWebsitePreview();
    } catch (error) {
      addError(error, { context: 'useAutoPopulationWithDataRefetch', operation: 'refetchAfterAutoPopulation' });
    } finally {
      setIsRefetching(false);
    }
  });

  const result = useAutoPopulation({
    mode,
    context,
    validateIsEnoughTimeElapsed,
    skipPolling,
    onComplete: handleOnComplete,
  });

  // Flash prevention: detect PENDING → COMPLETED during render (before paint)
  // to keep showing loading state until refetch finishes.
  const prevStatusRef = useRef<AutoPopulationPollingStatus>(result.status);
  if (prevStatusRef.current === 'PENDING' && result.status === 'COMPLETED') {
    setIsRefetching(true);
  }
  prevStatusRef.current = result.status;

  // Safety valve: when COMPLETED with 0 results, onComplete won't fire
  // (useAutoPopulation guards on pollResults.length > 0), so clear isRefetching here.
  useEffect(() => {
    if (isRefetching && result.status === 'COMPLETED' && result.results.length === 0) {
      setIsRefetching(false);
    }
  }, [isRefetching, result.status, result.results.length]);

  const status: AutoPopulationPollingStatus = isRefetching ? 'PENDING' : result.status;

  return {
    startAutoPopulation: result.startAutoPopulation,
    onComplete: result.onComplete,
    status,
    isAutoPopulationDone: result.isAutoPopulationDone,
    results: result.results,
  };
};
